import {
  APIClient,
  HttpMethod,
  RecoveryCoordinator,
  UnableRefreshTokenAPIError,
  usePrevioCore,
} from '@previo/core'
import type { Auth } from '~/http/auth/types'
import { APIPaths } from '~/http/APIPaths'
import { useAuthenticationStore } from '~/store/authentication'

const { useLocale } = usePrevioCore()

const lazyGoToPrevioIDLogin = async () => {
  // import lazily to prevent circular-dependencies
  const { goToPrevioIDLogin } = await import('~/composables/PrevioID')
  goToPrevioIDLogin()
}

export class AuthRecovery extends RecoveryCoordinator<string> {
  private lastRefreshedAccessToken: string | null = null

  constructor(private readonly apiClient: APIClient) {
    super()
  }

  protected async recover(failedAccessToken: string): Promise<void> {
    if (failedAccessToken === this.lastRefreshedAccessToken) {
      // Recovery has been triggered by a failing request that used a known expired/invalid access token.
      // This can happen with requests that start before a refresh, but don't finish until after refresh is done.
      // In this case, we don't need to refresh again and can just resolve right away.
      // (Doing so will simply re-send the request, but with a fresh access token the store already has.)
      return Promise.resolve()
    }

    this.lastRefreshedAccessToken = failedAccessToken

    try {
      const response = await this.apiClient.call<Auth>({
        method: HttpMethod.POST,
        path: APIPaths.USER_AUTH_REFRESH,
        pathParams: {},
        query: {},
        body: {
          token: useAuthenticationStore.value.refreshToken,
        },
        headers: {
          'Accept-Language': useLocale().get(),
        },
      })

      useAuthenticationStore.value = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      }
    } catch (e) {
      useAuthenticationStore.value = {
        accessToken: '',
        refreshToken: '',
      }

      await lazyGoToPrevioIDLogin()

      throw new UnableRefreshTokenAPIError({ cause: e })
    }
  }
}
